import {
  FlexBffResponse,
  AnalyticsPayload,
  FlexContext,
  HrefLang,
  LanguageCanonical,
  TrackingContext,
} from "typings/flexFramework/FlexDefinitions";

export interface FlexNamedMeta {
  keywords: string;
  "data-fm-seo": string;
  "flex:page-type": string;
  "flex:template-id": string;
  "flex:template-fm-id": string;
  "flex:template-version": string;
  ESSClientID: string;
  "ewe:page": string;
  google: string;
}

export interface FlexPropertyMeta {
  "og:title": string;
  "og:description": string;
  "og:locale": string;
  "og:site_name": string;
  "og:url": string;
  "og:image": string;
  "og:image:type": string;
  "og:type": string;
}

/**
 * This is the base class the describes the important SEO metadata for a FlexBffResponse.
 * It is used to implement different cases given a Composition or a Template.
 *
 * The implementation of this base class is used by SeoUtils.ts to create critical SEO meta tags in the head of the browser for Search Engines.
 * This functionality is powered by Bernie's fetchPageData in the controller.
 */
export abstract class BaseFlexSeoResponse implements FlexBffResponse {
  public analyticsPayload: AnalyticsPayload;

  public canonicalPath: string;

  public robotDirectives: string;

  public flexClientId: string;

  public flexContext: FlexContext;

  public hrefLangs: HrefLang[];

  public hrefNextPage: string;

  public hrefPreviousPage: string;

  public languageCanonicals: LanguageCanonical[];

  public structuredData: string;

  public templateId: string;

  public trackingContext: TrackingContext;

  public sitelinkOffForWotif: string;

  public abstract getKeywords: () => string;

  public abstract getDataFmSeo: () => string;

  public abstract getTemplateId: () => string;

  public abstract getTemplateFmId: () => string;

  public abstract getTemplateVersion: () => string;

  public abstract getOgImage: () => string;

  protected parseMediaUrl(mediaUrl: string): string {
    if (mediaUrl) {
      return mediaUrl.startsWith("http") ? mediaUrl : `https:${mediaUrl}`;
    }

    return "";
  }

  public abstract getTitle: () => string;

  public abstract getDescription: () => string;

  public abstract getRobots: () => string;

  public abstract getSiteLinkForWotif: () => string;

  protected constructor(flexBffResponse: FlexBffResponse) {
    this.analyticsPayload = flexBffResponse.analyticsPayload;
    this.canonicalPath = flexBffResponse.canonicalPath;
    this.robotDirectives = flexBffResponse.robotDirectives;
    this.flexClientId = flexBffResponse.flexClientId;
    this.flexContext = flexBffResponse.flexContext;
    this.hrefLangs = flexBffResponse.hrefLangs;
    this.hrefNextPage = flexBffResponse.hrefNextPage;
    this.hrefPreviousPage = flexBffResponse.hrefPreviousPage;
    this.languageCanonicals = flexBffResponse.languageCanonicals;
    this.structuredData = flexBffResponse.structuredData;
    this.templateId = flexBffResponse.templateId;
    this.trackingContext = flexBffResponse.trackingContext;
    this.sitelinkOffForWotif = flexBffResponse.sitelinkOffForWotif;
  }

  public hasEnoughData(): boolean {
    return this.flexContext && !!this.trackingContext;
  }

  /**
   * Represents a <meta name="apple" content="potato" />
   * Contains hard-coded values which are flex specific tags carried over from LPT.
   */
  public getNamedMeta = (): FlexNamedMeta => {
    return {
      keywords: this.getKeywords(),
      "data-fm-seo": this.getDataFmSeo(),
      "flex:page-type": this.getFlexPageType(),
      "flex:template-id": this.getTemplateId(),
      "flex:template-fm-id": this.getTemplateFmId(),
      "flex:template-version": this.getTemplateVersion(),
      ESSClientID: this.flexClientId,
      "ewe:page": "FLEX",
      google: this.getSiteLinkForWotif(),
    };
  };

  /**
   * Represents a <meta property="apple" content="potato"/>
   * Contains hard-coded values. See https://ogp.me/ for more info.
   */
  public getPropertyMeta = (): FlexPropertyMeta => {
    return {
      "og:title": this.getTitle(),
      "og:description": this.getDescription(),
      "og:locale": this.getResolvedLocale(),
      "og:site_name": this.getSiteNameForDisplay(),
      "og:url": this.getCanonicalUrl(),
      "og:image": this.getOgImage(),
      "og:image:type": "image/jpeg",
      "og:type": "website",
    };
  };

  public getCanonicalUrl = () => {
    return `https://${this.flexContext.siteContext.defaultDomain}${this.canonicalPath}`;
  };

  public getFlexPageType = () => this.flexContext.searchContext.pageType;

  public getResolvedLocale = () => this.flexContext.siteContext.resolvedLocale;

  public getSiteNameForDisplay = () => this.flexContext.siteContext.siteNameForDisplay;

  public getPageName = () => this.trackingContext.pageName;
}
